<template>
  <Dialog 
    v-model:visible="visible" 
    header="Code Draft History Snapshots" 
    :modal="true" 
    class="w-full max-w-2xl bg-slate-900 border border-slate-800 text-slate-100"
  >
    <div class="space-y-4 p-2 text-slate-300 text-xs">
      <p class="text-slate-400">
        Review past autosaved and execution snapshots for this question. Select a snapshot to preview and restore it to your editor.
      </p>

      <div v-if="!snapshots?.length" class="text-slate-500 py-8 text-center italic">
        No snapshots recorded yet. Run or save code to create snapshots.
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <!-- Snapshots List Sidebar -->
        <div class="space-y-1.5 overflow-y-auto max-h-80 pr-1">
          <div 
            v-for="snap in snapshots" 
            :key="snap.id"
            @click="selectedSnap = snap"
            :class="[
              'p-2.5 rounded-lg border text-left cursor-pointer transition-colors',
              selectedSnap?.id === snap.id 
                ? 'bg-slate-800 border-unibo-red text-white' 
                : 'bg-slate-950/60 border-slate-800 hover:bg-slate-800/50'
            ]"
          >
            <div class="flex items-center justify-between font-mono text-[10px]">
              <span class="text-amber-400 font-semibold">{{ snap.note || 'Snapshot' }}</span>
              <span class="text-slate-500">{{ new Date(snap.timestamp).toLocaleTimeString() }}</span>
            </div>
            <div class="text-[10px] text-slate-400 font-mono mt-1 truncate">
              {{ snap.code.substring(0, 40) }}...
            </div>
          </div>
        </div>

        <!-- Selected Snapshot Code Preview -->
        <div class="md:col-span-2 bg-slate-950 p-3 rounded-xl border border-slate-800 flex flex-col h-80">
          <div v-if="selectedSnap" class="flex flex-col h-full space-y-2">
            <div class="flex items-center justify-between border-b border-slate-800 pb-2">
              <span class="font-mono text-[11px] text-slate-400">
                Snapshot from {{ new Date(selectedSnap.timestamp).toLocaleString() }}
              </span>
              <Button 
                @click="restore(selectedSnap.code)" 
                severity="primary" 
                size="small"
                class="!bg-unibo-red hover:!bg-unibo-darkRed !py-1 !text-xs"
              >
                <i class="pi pi-history mr-1"></i> Restore to Editor
              </Button>
            </div>
            <pre class="flex-1 font-mono text-xs overflow-auto text-slate-300 leading-relaxed p-1">{{ selectedSnap.code }}</pre>
          </div>
          <div v-else class="text-slate-500 flex items-center justify-center h-full italic">
            Select a snapshot from the list to preview.
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { CodeSnapshot } from '../types';

const props = defineProps<{
  visible: boolean;
  snapshots?: CodeSnapshot[];
}>();

const emit = defineEmits(['update:visible', 'restore']);

const visible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val),
});

const selectedSnap = ref<CodeSnapshot | null>(null);

watch(() => props.snapshots, (snaps) => {
  if (snaps && snaps.length > 0) {
    selectedSnap.value = snaps[0];
  }
}, { immediate: true });

function restore(code: string) {
  emit('restore', code);
  visible.value = false;
}
</script>
